// apps/lk/src/components/dashboard/SchwartzBlock.tsx
'use client';
import RadarChart from './RadarChart';
import { scoreColor, scoreBg } from './polarity';

const VALUE_NAMES: Record<string, string> = {
  self_direction: 'Самостоятельность',
  stimulation: 'Стимуляция',
  hedonism: 'Гедонизм',
  achievement: 'Достижения',
  power: 'Власть',
  security: 'Безопасность',
  conformity: 'Конформность',
  tradition: 'Традиции',
  benevolence: 'Благожелательность',
  universalism: 'Универсализм',
};

const VALUE_DESC: Record<string, string> = {
  self_direction: 'Независимость мышления и выбора действий',
  stimulation: 'Поиск новизны, риска, азарта в жизни',
  hedonism: 'Наслаждение и чувственное удовольствие',
  achievement: 'Успех через проявление способностей',
  power: 'Контроль над людьми и ресурсами',
  security: 'Стабильность и безопасность общества и отношений',
  conformity: 'Сдерживание действий, нарушающих нормы',
  tradition: 'Уважение традиций и обычаев',
  benevolence: 'Забота о благополучии близких',
  universalism: 'Понимание, терпимость, защита блага всех людей',
};

const AXIS_NAMES: Record<string, string> = {
  openness_to_change: 'Открытость изменениям',
  self_enhancement: 'Самовозвышение',
  conservation: 'Сохранение',
  self_transcendence: 'Самотрансценденция',
};

const AXIS_PAIRS = [
  { left: 'openness_to_change', right: 'conservation', leftLabel: 'Открытость', rightLabel: 'Сохранение' },
  { left: 'self_transcendence', right: 'self_enhancement', leftLabel: 'На других', rightLabel: 'На себя' },
];

export function SchwartzBlock({ data }: { data: any }) {
  if (!data?.baseValues) return null;
  const bv = data.baseValues;
  const axes = data.axes ?? {};

  const radarPoints = Object.keys(VALUE_NAMES).map(k => ({
    label: VALUE_NAMES[k],
    value: bv[k] ?? 0,
  }));

  // Топ-3 ценности
  const sorted = Object.entries(bv)
    .sort(([, a], [, b]) => (b as number) - (a as number));

  return (
    <section className="mb-8">
      <h2 className="mb-4 text-lg font-bold text-slate-900">Ценности (Schwartz)</h2>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Радар 10 ценностей */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">10 базовых ценностей</h3>
          {radarPoints.length >= 3 ? (
            <RadarChart points={radarPoints} size={260} />
          ) : (
            <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">Недостаточно данных</div>
          )}
        </div>

        {/* Список ценностей с барами */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Ваши ценности</h3>
          <div className="space-y-2.5">
            {sorted.map(([key, val]) => (
              <div key={key}>
                <div className="mb-0.5 flex items-center justify-between">
                  <span className="text-sm text-slate-700">{VALUE_NAMES[key] ?? key}</span>
                  <span className={`text-xs font-bold ${scoreColor(val as number)}`}>{val as number}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full ${scoreBg(val as number)}`} style={{ width: `${val}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ось контрастов — компактные биполярные индикаторы */}
      {axes && Object.keys(axes).length >= 4 && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-4 text-sm font-semibold text-slate-700">Базовые ценностные конфликты</h3>
          <div className="space-y-4">
            {AXIS_PAIRS.map(pair => (
              <div key={pair.left}>
                <div className="mb-1 flex justify-between text-xs text-slate-500">
                  <span>{pair.leftLabel}</span>
                  <span>{pair.rightLabel}</span>
                </div>
                <BipolarBar left={axes[pair.left]} right={axes[pair.right]} />
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">
            Топ-приоритет: <span className="font-medium text-slate-700">{data.primaryValue ? (VALUE_NAMES[data.primaryValue] ?? data.primaryValue) : '—'}</span>
          </p>
        </div>
      )}
    </section>
  );
}

function BipolarBar({ left, right }: { left: number; right: number }) {
  const total = left + right || 1;
  const leftPct = Math.round((left / total) * 100);
  return (
    <div className="flex items-center gap-2">
      <span className="w-10 text-right text-xs font-medium text-indigo-600">{left}</span>
      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div className="absolute left-1/2 top-0 h-full w-px bg-slate-300" />
        <div className="h-full rounded-l-full bg-indigo-400" style={{ width: `${leftPct}%`, marginRight: 'auto' }} />
      </div>
      <span className="w-10 text-xs font-medium text-rose-500">{right}</span>
    </div>
  );
}
